import * as React from 'react'
import { Link, Outlet } from 'react-router-dom'
import {
  Building2,
  FileText,
  Handshake,
  Landmark,
  Menu,
  ListChecks,
  Percent,
  Scale,
  Settings,
} from 'lucide-react'
import {
  AppShell,
  IconButton,
  ProfileMenu,
  Select,
  type AppShellNavItem,
} from '@app/ui'
import { useAuth } from '@/auth/AuthContext'
import { DeployRefreshBanner } from '@/components/DeployRefreshBanner'
import { isApiError } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'

/**
 * Authenticated app shell: left primary nav rail, top session chrome, content.
 * Pages provide their own padding (list) or full-bleed entity chrome (loan).
 */
export function AppLayout() {
  const { user, logout, switchCompany, isPlatformAdmin } = useAuth()
  const memberships = user?.memberships ?? []
  const showCompanySwitcher = memberships.length > 1
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState(false)

  const nav = React.useMemo((): AppShellNavItem[] => {
    const items: AppShellNavItem[] = [
      { to: '/loans', label: 'Loans', icon: <FileText /> },
      { to: '/companies', label: 'Companies', icon: <Building2 /> },
    ]
    if (isPlatformAdmin) {
      items.push({
        to: '/admin/servicers',
        label: 'Servicers',
        icon: <Handshake />,
      })
      items.push({
        to: '/admin/title-companies',
        label: 'Title companies',
        icon: <Landmark />,
      })
      items.push({
        to: '/admin/attorneys',
        label: 'Attorneys',
        icon: <Scale />,
      })
      items.push({
        to: '/admin/apor',
        label: 'APOR',
        icon: <Percent />,
      })
      items.push({
        to: '/admin/validation-rules',
        label: 'Validation rules',
        icon: <ListChecks />,
      })
      items.push({
        to: '/admin/settings',
        label: 'Settings',
        icon: <Settings />,
      })
    }
    return items
  }, [isPlatformAdmin])

  async function handleCompanyChange(companyId: string): Promise<void> {
    if (!companyId) return
    try {
      await switchCompany(companyId)
    } catch (error) {
      logger.error('Company switch failed', {
        error: isApiError(error)
          ? (error.body.message ?? toErrorMessage(error))
          : toErrorMessage(error),
      })
    }
  }

  return (
    <AppShell
      title="Origination"
      banner={<DeployRefreshBanner />}
      brand={
        <Link
          to="/loans"
          className="flex min-w-0 items-center justify-center gap-2"
          title="Pixel Mortgage"
        >
          <FileText className="size-5 shrink-0 text-ds-brand" aria-hidden />
          {!collapsed || mobileOpen ? (
            <span className="truncate text-ds-sm font-semibold text-ds-text">
              Pixel Mortgage
            </span>
          ) : null}
        </Link>
      }
      nav={nav}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      mobileOpen={mobileOpen}
      onMobileOpenChange={setMobileOpen}
      topBar={
        <div className="flex h-12 items-center gap-3 border-b border-ds-border bg-ds-surface px-3 sm:px-4">
          <IconButton
            label="Open navigation"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="size-4" />
          </IconButton>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
            {showCompanySwitcher ? (
              <div className="w-40 sm:w-48">
                <Select
                  aria-label="Active company"
                  value={user?.currentCompanyId ?? ''}
                  onChange={(e) => void handleCompanyChange(e.target.value)}
                  options={memberships.map((m) => ({
                    value: m.companyId,
                    label: m.companyName,
                  }))}
                />
              </div>
            ) : null}
            {isPlatformAdmin ? (
              <span className="hidden items-center gap-1 text-ds-xs text-ds-muted md:inline-flex">
                <Building2 className="size-3.5" />
                Admin
              </span>
            ) : null}
            {user ? (
              <ProfileMenu
                user={{
                  name: user.name,
                  ...(user.email ? { email: user.email } : {}),
                }}
                onSignOut={logout}
                side="bottom"
                align="end"
              />
            ) : null}
          </div>
        </div>
      }
    >
      <Outlet />
    </AppShell>
  )
}
